import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AgTableClient } from "app/shared/AgTable";
import { apiGet, apiPost } from "app/services/apiServices";
import PageHeader from "app/shared/PageHeader";
import Loader from "app/shared/Loader";
import { Button, Input, Modal, message } from "antd";
import { EditOutlined, DeleteOutlined } from "@ant-design/icons";

const API_ENDPOINT_VENDOR_MASTER = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/vendorMaster";
const API_ENDPOINT_VENDOR_MASTER_SAVE = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/updateVendorMaster";
const API_ENDPOINT_VENDOR_MASTER_ADD = "https://stage-apiplatform.finkraftai.com/api/hotelFollowUp/addVendorMaster";

const NumericCellEditor = forwardRef((props: any, ref) => {
  const [value, setValue] = useState(String(props.value ?? ""));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  useImperativeHandle(ref, () => ({
    getValue: () => value,
  }));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const allowed = ["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab", "Enter"];
    if (props.allowComma) allowed.push(",");
    if (!allowed.includes(e.key) && !/^\d$/.test(e.key)) e.preventDefault();
  };

  return (
    <input
      ref={inputRef}
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={handleKeyDown}
      style={{ width: "100%", height: "100%", border: "none", outline: "none", padding: "0 8px", fontSize: 13 }}
    />
  );
});

let tempIdCounter = 0;

const emptyCell = (params: any) => {
  if (params?.node?.group) return params.value;
  const value = Array.isArray(params.value) ? params.value.join(", ") : params.value;
  const isEmpty = value === null || value === undefined || String(value).trim() === "";
  return <span style={{ color: isEmpty ? "#aaa" : "#000" }}>{isEmpty ? "-" : value}</span>;
};


function VendorMaster() {
  const [rowData, setRowData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [aliasPopover, setAliasPopover] = useState<{ visible: boolean; rowData: any }>({ visible: false, rowData: null });
  const [aliasInput, setAliasInput] = useState("");
  const hasFetched = useRef(false);
  const gridRef = useRef<any>(null);
  const changedRowsRef = useRef<Map<string, any>>(new Map());
  const newRowsRef = useRef<Map<string, any>>(new Map());
  const [messageApi, contextHolder] = message.useMessage();

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchData = async () => {
      try {
        const response = await apiGet(API_ENDPOINT_VENDOR_MASTER);
        const data = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : [];
        setRowData(data);
      } catch (error) {
        setRowData([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const showSaveBtn = () => {
    const saveBtn = document.getElementById("vendorSaveBtn");
    if (saveBtn) saveBtn.style.display = "flex";
  };

  const hideSaveBtn = () => {
    const saveBtn = document.getElementById("vendorSaveBtn");
    if (saveBtn) saveBtn.style.display = "none";
  };

  const handleAddMore = () => {
    const tempId = `new_${++tempIdCounter}`;
    const newRow = { _tempId: tempId };
    gridRef.current?.applyTransaction({ add: [newRow], addIndex: 0 });
    newRowsRef.current.set(tempId, newRow);
    showSaveBtn();
  };

  const handleCellEditingStarted = () => {
    showSaveBtn();
  };

  const handleRemoveRow = (row: any) => {
    gridRef.current?.applyTransaction({ remove: [row] });
    if (row._tempId) newRowsRef.current.delete(row._tempId);
    if (newRowsRef.current.size === 0 && changedRowsRef.current.size === 0) {
      hideSaveBtn();
    }
  };

  const handleAliasSave = async () => {
    if (!aliasInput.trim()) {
      messageApi.error("Alias text cannot be empty.");
      return;
    }
    const row = aliasPopover.rowData;
    try {
      await apiPost(API_ENDPOINT_VENDOR_MASTER_ADD, {
        alias_only: true,
        data: [{ hotel_uid: row.hotel_uid, gstin: row.gstin, alias_text: aliasInput.trim() }],
      });
      const newAlias = { alias_text: aliasInput.trim() };
      const updatedRow = {
        ...row,
        aliases: [...(Array.isArray(row.aliases) ? row.aliases : []), newAlias],
      };
      setRowData((prev) =>
        prev.map((r) => (r.hotel_uid === row.hotel_uid ? updatedRow : r))
      );
      gridRef.current?.applyTransaction({ update: [updatedRow] });
      messageApi.success("Alias added successfully!");
      setAliasPopover({ visible: false, rowData: null });
      setAliasInput("");
    } catch (error) {
      messageApi.error("Failed to add alias.");
    }
  };

  const AliasCellRenderer = (params: any) => {
    if (params?.node?.group) return params.value;
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        {params.value && <span style={{ color: "#000" }}>{params.value}</span>}
        <EditOutlined
          style={{ color: "#0a8394", cursor: "pointer", fontSize: 12 }}
          onClick={(e) => {
            e.stopPropagation();
            setAliasPopover({ visible: true, rowData: params.data });
            setAliasInput("");
          }}
        />
      </div>
    );
  };

  const columnDefs = useMemo(() => [
  
    { headerName: "Hotel Name",
      field: "legal_name",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      editable: true,
      cellRenderer: emptyCell
    },
    { headerName: "Trade Name",
      field: "trade_name",
      enableRowGroup: true,
      filter: "agTextColumnFilter",
      editable: true,
      cellRenderer: emptyCell
    },
    { headerName: "Hotel GSTIN",
      field: "gstin",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      editable: true,
      cellRenderer: emptyCell
    },
    { headerName: "Locality",
      field: "locality",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      editable: true,
      cellRenderer: emptyCell
    },
    { headerName: "City",
      field: "city",
      filter: "agTextColumnFilter",
      enableRowGroup: true,
      editable: true,
      cellRenderer: emptyCell
    },
    { headerName: "State",
      field: "state",
      filter: "agTextColumnFilter",
      editable: true,
      enableRowGroup: true,
      cellRenderer: emptyCell
    },
    { headerName: "Pincode",
      field: "pincode",
      filter: "agTextColumnFilter",
      editable: true,
      enableRowGroup: true,
      cellRenderer: emptyCell,
      cellEditor: NumericCellEditor,
      cellEditorParams: { allowComma: false },
    },
    {
      headerName: "Emails",
      field: "emails",
      filter: "agTextColumnFilter",
      editable: true,
      enableRowGroup: true,
      cellRenderer: emptyCell,
      valueParser: (params: any): string[] => {
        if (!params.newValue) return [];
        if (Array.isArray(params.newValue)) return params.newValue;
        return String(params.newValue).split(",").map((s: string) => s.trim()).filter(Boolean);
      },
    },
    {
      headerName: "Phone Number",
      field: "phone_numbers",
      filter: "agTextColumnFilter",
      editable: true,
      enableRowGroup: true,
      cellRenderer: emptyCell,
      cellEditor: NumericCellEditor,
      cellEditorParams: { allowComma: true },
      valueParser: (params: any): string[] => {
        if (!params.newValue) return [];
        if (Array.isArray(params.newValue)) return params.newValue;
        return String(params.newValue)
          .split(",")
          .map((s: string) => s.trim().replace(/\D/g, ""))
          .filter(Boolean);
      },
    },
    { headerName: "Hotel Address",
      field: "address_full",
      filter: "agTextColumnFilter",
      editable: true,
      enableRowGroup: true,
      cellRenderer: emptyCell
    },
    {
      headerName: "Alias Text", field: "aliases", filter: "agTextColumnFilter", editable: false,
      valueGetter: (params: any) => {
        const aliases = params.data?.aliases;
        if (!Array.isArray(aliases) || aliases.length === 0) return "";
        return aliases.map((a: any) => a.alias_text).join(", ");
      },
      cellRenderer: AliasCellRenderer,
    },
    { field: "updated_at", headerName: "Updated At", formatType: "DATE", filter: "agDateColumnFilter" },
      {
      headerName: "Action",
      field: "_action",
      cellRenderer: (params: any) => {
        if (params?.node?.group || !params.data?._tempId) return null;
        return (
          <DeleteOutlined
            style={{ color: "#ff4d4f", cursor: "pointer" }}
            onClick={() => handleRemoveRow(params.data)}
          />
        );
      },
    },
  ], [AliasCellRenderer, handleRemoveRow]);

  const handleCellValueChanged = (event: any) => {
    const tempId = event.data?._tempId;
    const uid = event.data?.hotel_uid;

    const fieldName = event.colDef.field === "aliases" ? "alias_text" : event.colDef.field;

    if (tempId) {
      const existing = newRowsRef.current.get(tempId) || { _tempId: tempId };
      existing[fieldName] = event.newValue;
      newRowsRef.current.set(tempId, existing);
    } else if (uid) {
      const existing = changedRowsRef.current.get(uid) || { hotel_uid: uid };
      existing[fieldName] = event.newValue;
      changedRowsRef.current.set(uid, existing);
    }
  };

  const validateRows = (rows: any[], skipFields: string[]) => {
    const emptyFields: string[] = [];
    for (const row of rows) {
      for (const [field, value] of Object.entries(row)) {
        if (skipFields.includes(field)) continue;
        const isEmpty = value === null || value === undefined || String(value).trim() === "";
        if (isEmpty) emptyFields.push(field);
      }
    }
    return emptyFields;
  };

  const handleSave = async () => {
    const modifiedRows = Array.from(changedRowsRef.current.values());
    const newRows = Array.from(newRowsRef.current.values()).map(({ _tempId, ...rest }: any) => rest);

    if (modifiedRows.length === 0 && newRows.length === 0) {
      messageApi.info("No changes to save.");
      return;
    }

    const emptyInModified = validateRows(modifiedRows, ["hotel_uid"]);
    const emptyInNew = validateRows(newRows, []);
    const allEmpty = Array.from(new Set(emptyInModified.concat(emptyInNew)));

    if (allEmpty.length > 0) {
      const msg = allEmpty.length === 1
        ? `"${allEmpty[0]}" cannot be empty.`
        : `These fields cannot be empty: ${allEmpty.join(", ")}`;
      messageApi.error(msg);
      return;
    }

    setIsSaving(true);
    try {
      if (modifiedRows.length > 0) {
        const aliasUpdates: any[] = [];
        const fieldUpdates: any[] = [];

        modifiedRows.forEach((row) => {
          const { alias_text, ...rest } = row;
          if (alias_text !== undefined) {
            const original = rowData.find((r: any) => r.hotel_uid === row.hotel_uid);
            aliasUpdates.push({ hotel_uid: row.hotel_uid, gstin: original?.gstin, alias_text });
          }
          if (Object.keys(rest).length > 1) {
            fieldUpdates.push(rest);
          }
        });

        if (fieldUpdates.length > 0) {
          const res = await apiPost(API_ENDPOINT_VENDOR_MASTER_SAVE, fieldUpdates);
          if (res?.status === false) throw new Error(res?.message || "Save failed");
        }
        if (aliasUpdates.length > 0) {
          const aliasPayload = aliasUpdates.map(({ alias_only: _ao, ...rest }: any) => rest);
          const res = await apiPost(API_ENDPOINT_VENDOR_MASTER_ADD, { alias_only: true, data: aliasPayload });
          if (res?.status === false) throw new Error(res?.message || "Save failed");
        }
      }
      if (newRows.length > 0) {
        const res = await apiPost(API_ENDPOINT_VENDOR_MASTER_ADD, newRows);
        if (res?.status === false) throw new Error(res?.message || "Save failed");
      }
      messageApi.success("Changes saved successfully!");
      changedRowsRef.current.clear();
      newRowsRef.current.clear();
      hideSaveBtn();
    } catch (error: any) {
      const isJsonError = error?.message?.toLowerCase().includes("json") || error instanceof SyntaxError;
      messageApi.error(isJsonError ? "Failed to save changes. Please try again." : (error?.message || "Failed to save changes. Please try again."));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div style={{ height: "100%", width: "100%", display: "flex", flexDirection: "column" }}>
      {contextHolder}
      <PageHeader
        title="Vendor Master Data"
        showWorkspaceDropdown={false}
        rightActions={
          <div style={{ display: "flex", gap: 8 }}>
            <Button size="small" onClick={handleAddMore} style={{ borderColor: "#0a8394", color: "#0a8394" }}>
              Add More
            </Button>
            <Button
              id="vendorSaveBtn"
              type="primary"
              size="small"
              onClick={handleSave}
              loading={isSaving}
              disabled={isSaving}
              style={{ display: "none" }}
            >
              {isSaving ? "Saving..." : "Save"}
            </Button>
          </div>
        }
      />
      <Modal
        title="Add Alias Text"
        open={aliasPopover.visible}
        onCancel={() => { setAliasPopover({ visible: false, rowData: null }); setAliasInput(""); }}
        footer={[
          <Button key="cancel" onClick={() => { setAliasPopover({ visible: false, rowData: null }); setAliasInput(""); }}>Cancel</Button>,
          <Button key="update" type="primary" onClick={handleAliasSave}>Update</Button>,
        ]}
      >
        <Input
          placeholder="Enter alias text"
          value={aliasInput}
          onChange={(e) => setAliasInput(e.target.value)}
          onPressEnter={handleAliasSave}
          autoFocus
        />
      </Modal>

      {isLoading ? (
        <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Loader />
        </div>
      ) : (
        <div style={{ flex: 1, position: "relative" }}>
          <AgTableClient
            ref={gridRef}
            // @ts-ignore
            rowData={rowData}
            columnDefs={columnDefs}
            groupPanel
            enableDirectEdit
            singleClickEdit={true}
            onCellEditingStarted={handleCellEditingStarted}
            onCellValueChanged={handleCellValueChanged}
            getRowId={(params: any) => params.data.hotel_uid || params.data._tempId}
          />
        </div>
      )}
    </div>
  );
}

export default VendorMaster;
